/**
 * Reprovar a identificação — o financeiro devolve à cobrança o que foi sinalizado.
 *
 * Irmã da "Aprovar Identificação" e com os MESMOS critérios de ativação: as duas agem sobre o que
 * está sinalizado, uma efetivando e outra desfazendo. É a saída para quando o financeiro confere e
 * o de-para está errado — título trocado, valor rateado de outro jeito, pagamento de outro cliente.
 *
 * ⚠️ O que já foi EFETIVADO não é afetado, e não por uma exceção: a efetivação apaga a marcação, e
 * esta ação só alcança marcação viva. O dialog diz isso na cara, porque "reprovar identificação"
 * soa como desfazer tudo — inclusive o que já virou pagamento.
 *
 * O motivo é obrigatório. Quem vai perguntar "por que minha marcação sumiu?" é a própria cobrança,
 * dias depois, olhando o histórico da FATURA — e a resposta tem de estar lá. Pedir depois não
 * funciona.
 */
import { useState } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { PaymentRow, unmarkInvoices } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';

interface Props {
    payment: PaymentRow;
    onClose: () => void;
    onRejected: () => void;
}

const RejectIdentificationDialog: React.FC<Props> = ({ payment, onClose, onRejected }) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const markedCount = payment.scheduledCount ?? payment.linkedInvoicesCount ?? 0;
    const settledCount = payment.paidCount ?? 0;

    const confirm = async () => {
        setSubmitting(true);
        try {
            const result = await unmarkInvoices(payment.paymentId, reason.trim());
            const howMany = result?.unmarked?.length ?? markedCount;
            toast.success(howMany > 1
                ? `Identificação reprovada — sinalização removida de ${howMany} faturas.`
                : 'Identificação reprovada — sinalização removida.');
            onRejected();
            onClose();
        } catch (e: any) {
            toast.error(`Não foi possível reprovar: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <FaExclamationTriangle className="text-amber-500" /> Reprovar identificação
                    </h3>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 space-y-3">
                    <p className="text-sm text-gray-700 dark:text-gray-200">
                        Pagamento de <strong>{formatBRL(payment.amount || 0)}</strong>
                        {payment.payer?.name && <> · {payment.payer.name}</>}
                    </p>

                    <p className="text-sm px-3 py-2 rounded bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        {markedCount > 1
                            ? <>A sinalização de <strong>{markedCount} faturas</strong> será removida e elas
                                voltam a ficar sem marcação.</>
                            : <>A sinalização da fatura será removida e ela volta a ficar sem marcação.</>}
                        {' '}O pagamento volta para a fila de identificação.
                    </p>

                    {/* O aviso só aparece quando há efetivado: dito sempre, ensinaria a ignorá-lo */}
                    {settledCount > 0 && (
                        <p className="text-sm px-3 py-2 rounded bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                            {settledCount > 1
                                ? <><strong>{settledCount} faturas</strong> já efetivadas</>
                                : <><strong>1 fatura</strong> já efetivada</>}
                            {' '}neste pagamento <strong>não</strong> serão afetadas — elas já foram
                            pagas e o settlement já movimentou os títulos.
                        </p>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="motivo-reprovacao">
                            Motivo da reprovação *
                        </label>
                        <textarea
                            id="motivo-reprovacao"
                            className="w-full text-sm border rounded p-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                            rows={3}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="Por que esta identificação está sendo devolvida"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Vai para o histórico de cada fatura — é o que a cobrança vai ler quando
                            procurar a marcação que sumiu.
                        </p>
                    </div>
                </div>

                <div className="px-6 py-3 border-t dark:border-slate-600 flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    {/* sem motivo o servidor recusa de todo jeito — melhor o botão dizer antes da viagem */}
                    <button onClick={confirm} disabled={submitting || !reason.trim()}
                        className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                        {submitting ? 'Reprovando...' : 'Reprovar Identificação'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RejectIdentificationDialog;
